import { useEffect, useSyncExternalStore } from 'react'

const APP_NAME = 'Packs Admin'

/**
 * Titles claimed by whatever is open over the page -- a record sheet, today -- most
 * recent last.
 *
 * A store rather than each claimant writing `document.title` itself: effects run child
 * first, so the root's write would land after a sheet's and overwrite it on every
 * render. Here the root is the only writer, and a claim just makes it render again.
 */
const claims: string[] = []
const listeners = new Set<() => void>()

function emit() {
    for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

function latestClaim() {
    return claims.at(-1)
}

/** Makes the tab title more specific than the route while `title` is set; released on unmount or when cleared. */
export function useTitleOverride(title: string | undefined) {
    useEffect(() => {
        if (!title) return
        claims.push(title)
        emit()
        return () => {
            claims.splice(claims.lastIndexOf(title), 1)
            emit()
        }
    }, [title])
}

/** Most specific first: "Order a1b2c3 · Withdrawals · Packs Admin". */
export function composeTitle(claim: string | undefined, trail: string[]): string {
    return [claim, ...[...trail].reverse(), APP_NAME].filter(Boolean).join(' · ')
}

/**
 * The one writer of `document.title`, called once at the root with the breadcrumb
 * trail. Without it every tab in a browser full of admin pages read "Packs Admin".
 */
export function useDocumentTitle(trail: string[]) {
    const claim = useSyncExternalStore(subscribe, latestClaim)
    const title = composeTitle(claim, trail)

    useEffect(() => {
        document.title = title
    }, [title])
}
